import { useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Panel } from "@/components/KpiCard";
import { AutresDepensesDialog } from "@/components/AutresDepensesDialog";
import { beneficiaire, cleGroupe, slug } from "@/components/DeplacerOperation";
import { api, errMsg, money, moisLabel } from "@/lib/api";
import { estAssurance, taxePrime, TAUX_TAXE_PRIME } from "@/lib/assurance";

const DEP_COULEURS = ["#0F172A", "#B91C1C", "#D97706", "#047857", "#0369A1", "#7C3AED", "#BE185D", "#0D9488", "#65A30D", "#475569", "#C2410C", "#4338CA"];
const REV_COULEURS = ["#047857", "#0369A1", "#D97706", "#7C3AED", "#0D9488", "#BE185D", "#65A30D", "#475569"];
const pct = (v, total) => `${total ? Math.round((v / total) * 100) : 0} %`;
const grouper = (items) => Object.values(items.reduce((acc, t) => {
  const nom = beneficiaire(t.description);
  const k = cleGroupe(nom);
  const g = acc[k] || (acc[k] = { nom, montant: 0, items: [] });
  g.montant += t.montant;
  g.items.push(t);
  return acc;
}, {})).sort((a, b) => b.montant - a.montant);

const Pcts = ({ v, total, totalRevenus, cumul, testId }) => (
  <>
    <span className="w-12 text-right tabular text-xs text-slate-400">{pct(v, total)}</span>
    {totalRevenus !== undefined && <span className="w-14 text-right tabular text-xs text-slate-500" data-testid={testId}>{pct(v, totalRevenus)}</span>}
    {totalRevenus !== undefined && <span className="w-16 text-right tabular text-xs font-medium text-slate-700" data-testid={testId && `${testId}-cumul`}>{cumul === undefined ? "" : pct(cumul, totalRevenus)}</span>}
  </>
);

const Classer = ({ g, cl, mode, testId }) => {
  const [envoi, setEnvoi] = useState(false);
  const [cat, setCat] = useState(null);
  const envoyer = async (champs, quoi) => {
    const ids = [...new Set(g.items.map((t) => t.id))];
    if (champs.logement && ids.length > 1 && !window.confirm(`Rattacher les ${ids.length} opérations de « ${g.nom} » (${money(g.montant)}) à ${champs.logement.replace("immeuble:", "")} et le mémoriser pour les prochaines ?\n\nSi ces factures concernent plusieurs immeubles, rattachez-les une par une dans Commentaires opérations, ou créez des règles de montant dans Paramètres › Factures par immeuble.`)) { return; }
    setEnvoi(true);
    try {
      const { data } = await api.post("/transactions/commentaire-lot", { ids, champs });
      const annuler = async () => {
        try { await api.post("/transactions/commentaire-lot", { ids, champs: Object.fromEntries(Object.keys(champs).map((k) => [k, ""])) }); toast.success("Classement annulé"); cl.onClasse(); }
        catch (e) { toast.error(errMsg(e)); }
      };
      toast.success(`« ${g.nom} » ${quoi} : ${data.modifiees} opération(s), mémorisé pour les prochaines`, { action: { label: "Annuler", onClick: annuler }, duration: 10000 });
      cl.onClasse();
    } catch (e) { toast.error(errMsg(e)); setEnvoi(false); }
  };
  const choisirCat = (v) => {
    const c = cl.choix.find((x) => x.valeur === v);
    if (c?.par_immeuble && cl.immeubles.length) { setCat(c); } else { envoyer({ categorie: v }, `classé dans ${c?.libelle || v}`); }
  };
  const choisirImm = (imm) => envoyer({ ...(cat ? { categorie: cat.valeur } : {}), logement: `immeuble:${imm}` },
    cat ? `classé dans ${cat.libelle.replace(" (par immeuble)", "")} ${imm}` : `rattaché à ${imm}`);
  if (envoi) return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />;
  const trigger = "h-6 w-auto shrink-0 gap-1 border-slate-200 bg-white px-1.5 text-[11px] text-emerald-700";
  if (mode === "immeuble" || cat) {
    return (
      <Select key={cat?.valeur || "imm"} defaultOpen={!!cat} onValueChange={choisirImm} onOpenChange={(o) => { if (!o && cat) { setTimeout(() => setCat(null), 0); } }}>
        <SelectTrigger className={trigger} data-testid={`${testId}${cat ? "-immeuble" : ""}`}><SelectValue placeholder={cat ? `${cat.libelle.replace(" (par immeuble)", "")} · immeuble…` : "Immeuble…"} /></SelectTrigger>
        <SelectContent className="max-h-72">
          {cl.immeubles.map((n) => <SelectItem key={n} value={n} data-testid={`${testId}-imm-${slug(n)}`}>{n}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Select onValueChange={choisirCat}>
      <SelectTrigger className={trigger} data-testid={testId}>
        <SelectValue placeholder="Classer…" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {cl.choix.map((c) => <SelectItem key={c.valeur} value={c.valeur} data-testid={`${testId}-option-${slug(c.valeur)}`}>{c.libelle}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const Groupes = ({ items, total, totalRevenus, testId, onOuvrir, cl, mode }) => (
  <ul className="mb-1 ml-5 border-l border-slate-200 pl-2" data-testid={`${testId}-autres-groupes`}>
    {grouper(items).map((g, i) => (
      <li key={g.nom} className="flex flex-wrap items-center justify-end gap-1 sm:flex-nowrap">
        <button type="button" onClick={() => onOuvrir(g)} className="flex min-w-0 w-full flex-wrap items-center gap-x-2 rounded px-1 py-0.5 text-left text-xs transition-colors hover:bg-slate-50 sm:w-auto sm:flex-1 sm:flex-nowrap" data-testid={`${testId}-autres-groupe-${i}`}>
          <span className="min-w-0 w-full truncate text-slate-600 sm:w-auto sm:flex-1" title={g.nom}>{g.nom} <span className="text-slate-400">({g.items.length})</span></span>
          <span className="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap">
            <span className="tabular">{money(g.montant)}</span>
            <Pcts v={g.montant} total={total} totalRevenus={totalRevenus} />
          </span>
        </button>
        {cl && <Classer g={g} cl={cl} mode={mode} testId={`${testId}-autres-groupe-${i}-classer`} />}
      </li>
    ))}
  </ul>
);

const Ligne = ({ c, couleur, total, totalRevenus, cumul, testId, onOuvrir, groupes, onGroupe, cl }) => {
  const [ouvert, setOuvert] = useState(false);
  const id = `${testId}-${slug(c.categorie)}`;
  return (
    <li data-testid={id}>
      <div className="flex items-center gap-1">
        {groupes ? (
          <button type="button" onClick={() => setOuvert(!ouvert)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title={ouvert ? "Réduire" : "Développer par bénéficiaire"} data-testid={`${id}-developper`}>
            {ouvert ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : <span className="w-[18px] shrink-0" />}
        <button type="button" onClick={() => onOuvrir(c.categorie)} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-slate-50 sm:flex-nowrap" data-testid={`${id}-detail`}>
          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: couleur }} />
          <span className="min-w-0 w-[calc(100%-1.5rem)] truncate text-slate-700 hover:underline decoration-emerald-600 sm:w-auto sm:flex-1" title={c.categorie}>{c.categorie}</span>
          <span className="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap">
            <span className="tabular font-medium">{money(c.montant)}</span>
            <Pcts v={c.montant} total={total} totalRevenus={totalRevenus} cumul={cumul} testId={`${id}-pct-revenus`} />
          </span>
        </button>
      </div>
      {c.rubriques?.length > 1 && <p className="truncate pl-8 text-[11px] text-slate-400" title={c.rubriques.join(" · ")} data-testid={`${id}-rubriques`}>{c.rubriques.join(" · ")}</p>}
      {estAssurance(c.categorie) && (
        <p className="pl-8 text-[11px] text-slate-500" data-testid={`${id}-taxe-prime`}>dont taxe sur prime d'assurance ({TAUX_TAXE_PRIME} %) : {money(taxePrime(c.montant))}</p>
      )}
      {ouvert && groupes && <Groupes items={groupes} total={total} totalRevenus={totalRevenus} testId={`${id}`} onOuvrir={onGroupe} cl={cl}
        mode={cl?.rubriques.includes(c.categorie) ? "immeuble" : "categorie"} />}
    </li>
  );
};

const Anneau = ({ titre, rows, couleurs, onOuvrir, testId, vide, totalRevenus, autre, detail, onGroupe, cl }) => {
  const total = rows.reduce((s, r) => s + r.montant, 0);
  let run = 0;
  const cumuls = rows.map((r) => (run += r.montant));
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-mono uppercase tracking-wider text-slate-500">{titre}</h3>
        <span className="text-sm font-semibold tabular" data-testid={`${testId}-total`}>{money(total)}</span>
      </div>
      {!rows.length ? <p className="text-sm text-slate-500">{vide}</p> : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={rows} dataKey="montant" nameKey="categorie" innerRadius={55} outerRadius={90} paddingAngle={2} onClick={(e) => e?.categorie && onOuvrir(e.categorie)} className="cursor-pointer">
                {rows.map((c, i) => <Cell key={c.categorie} fill={couleurs[i % couleurs.length]} className="cursor-pointer" />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 flex items-center gap-2 border-b border-slate-100 pb-1 pl-6 pr-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">
            <span className="flex-1">Type</span><span>Montant</span>
            <span className="w-12 text-right">{totalRevenus !== undefined ? "% dép." : "%"}</span>
            {totalRevenus !== undefined && <span className="w-14 text-right">% rev.</span>}
            {totalRevenus !== undefined && <span className="w-16 text-right" title="Somme des % rev. depuis la plus grosse dépense">% rev. cumulé</span>}
          </div>
          <ul className="mt-1 space-y-0.5 text-sm max-h-96 overflow-y-auto pr-1">
            {rows.map((c, i) => <Ligne key={c.categorie} c={c} couleur={couleurs[i % couleurs.length]} total={total} totalRevenus={totalRevenus} cumul={cumuls[i]} testId={testId} onOuvrir={onOuvrir}
              groupes={(autre && c.categorie.startsWith(autre)) || cl?.rubriques.includes(c.categorie) ? detail[c.categorie] || [] : null} onGroupe={(g) => onGroupe(c.categorie, g)} cl={cl} />)}
          </ul>
        </>
      )}
    </div>
  );
};

export const RepartitionPanel = ({ depenses, depensesAbregees = [], revenus, detailDepenses, detailAbregees = {}, detailRevenus, mois, classement }) => {
  const [dialog, setDialog] = useState(null);
  const totalRevenus = revenus.reduce((s, r) => s + r.montant, 0);
  const periode = mois && mois !== "all" ? moisLabel(mois) : "Toutes les périodes";
  const items = dialog ? (dialog.items || (dialog.revenus ? detailRevenus : dialog.abrege ? detailAbregees : detailDepenses)[dialog.categorie] || []) : [];
  return (
    <Panel title="Répartition des dépenses et des revenus" testId="expenses-chart">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
        <Anneau titre="Dépenses par type" rows={depenses} couleurs={DEP_COULEURS} onOuvrir={(c) => setDialog({ categorie: c, revenus: false })} testId="repartition-depenses" vide="Aucune dépense." totalRevenus={totalRevenus}
          autre="Autres" detail={detailDepenses} cl={classement} onGroupe={(cat, g) => setDialog({ categorie: `${cat} › ${g.nom}`, revenus: false, items: g.items })} />
        <Anneau titre="Dépenses par type abrégé" rows={depensesAbregees} couleurs={DEP_COULEURS} onOuvrir={(c) => setDialog({ categorie: c, revenus: false, abrege: true })} testId="repartition-depenses-abrege" vide="Aucune dépense." totalRevenus={totalRevenus}
          autre={null} detail={detailAbregees} cl={null} onGroupe={() => {}} />
        <Anneau titre="Revenus par type" rows={revenus} couleurs={REV_COULEURS} onOuvrir={(c) => setDialog({ categorie: c, revenus: true })} testId="repartition-revenus" vide="Aucun revenu."
          autre="Autres revenus" detail={detailRevenus} onGroupe={(cat, g) => setDialog({ categorie: `${cat} › ${g.nom}`, revenus: true, items: g.items })} />
      </div>
      <p className="mt-4 text-xs text-slate-500">Types de dépenses et de revenus : mots-clés modifiables dans Paramètres. Cliquez sur un type (cercle ou nom) pour voir ses opérations et les télécharger en PDF ; la flèche à gauche des lignes « Autres … » les développe par bénéficiaire ; « Classer… » range un bénéficiaire de dépenses dans une rubrique (et son immeuble pour Hydro, assurance, taxes, Internet) et le mémorise pour les prochaines opérations ; sous « Hydro », « Assurance »… sans immeuble, « Immeuble… » rattache le bénéficiaire. Dans le détail d'une dépense ou d'un revenu, « Déplacer vers… » change la catégorie ou le type d'une opération (seule, ou tout son bénéficiaire). « % rev. » = part de la dépense dans le total des revenus ; « % rev. cumulé » = somme des % rev. depuis la plus grosse dépense (la dernière ligne = total des dépenses ÷ revenus). « Dépenses par type abrégé » regroupe les rubriques (immeubles, étages, honoraires…) ; sous chaque ligne, les rubriques regroupées ; « Autres » réunit le reste.</p>
      <AutresDepensesDialog open={!!dialog} items={items} onClose={() => setDialog(null)} revenus={!!dialog?.revenus} categorie={dialog?.categorie || ""}
        autre={dialog?.categorie === (dialog?.revenus ? "Autres revenus" : "Autres dépenses")} periode={periode} cl={dialog?.revenus ? (classement ? { ...classement, choix: classement.choixRevenus || [], champ: "type_revenu" } : null) : classement} recharger={classement?.onClasse || null} />
    </Panel>
  );
};
